import React, { useState, useEffect } from 'react';
import { ScrollView, StyleSheet, View, Switch } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useLanguage } from '../../locale';
import { Layout, Title, Paragraph } from '../../components';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import {
  useGetSurveyDetail,
  useSubmitSurveyAnswers,
} from '../../hooks/api/survey.api';
import { useGetMyDetails } from '../../hooks/api/user.api';
import useUserData from '../../hooks/useUserData';
import { SurveyAnswer, SurveyQuestion } from '../../api/survey/survey.models';
import {
  SliderQuestion,
  TextQuestion,
  SurveyDetailBottomBar,
  SurveyDetailSkeleton,
} from './components';
import { useBackNavbarAction } from '../../hooks/useBackNavbarAction';
import { useFullPageLoader } from '../../context/Loader/useFullPageLoader';
import { SURVEY_DETAIL_SCREEN } from '../../navigation/authenticated/authenticated.types';

type SurveyDetailScreenRouteProp = {
  key: string;
  name: typeof SURVEY_DETAIL_SCREEN;
  params: {
    surveyId: string;
  };
};

export const SurveyDetailScreen = () => {
  const [t, , currentLocale] = useLanguage();
  const navigation = useNavigation();
  const route = useRoute<SurveyDetailScreenRouteProp>();
  const { surveyId } = route.params;
  const { company } = useUserData();
  const companyId = company?.id;
  const [FeatureFlags] = useFeatureFlags();
  const [openLoader, closeLoader] = useFullPageLoader();

  // Redirect back if the PulseSurvey feature flag is disabled
  useEffect(() => {
    if (!FeatureFlags.PulseSurvey) {
      navigation.goBack();
    }
  }, [FeatureFlags.PulseSurvey, navigation]);

  // If feature flag is disabled, don't render anything while redirecting
  if (!FeatureFlags.PulseSurvey) {
    return null;
  }

  const {
    data: surveyDetailData,
    isLoading,
    refetch,
  } = useGetSurveyDetail(companyId, surveyId);
  const { mutate: submitAnswers, isPending: isSubmitting } =
    useSubmitSurveyAnswers();
  const { data: myDetails } = useGetMyDetails();

  // Initialize answers state
  const [answers, setAnswers] = useState<Record<string, number | string>>({});
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Load previous answers when survey detail data is available
  useEffect(() => {
    if (surveyDetailData?.item) {
      const item = surveyDetailData.item;

      // Set anonymous state if available, otherwise use user's default preference
      if (item && item.answerAnonymous !== undefined) {
        setIsAnonymous(item.answerAnonymous);
      } else if (myDetails?.voteAnonymous !== undefined) {
        setIsAnonymous(myDetails.voteAnonymous);
      } else {
        setIsAnonymous(false);
      }

      // Load previous answers if available
      if (item.userAnswers && Array.isArray(item.userAnswers)) {
        const previousAnswers: Record<string, number | string> = {};

        item.userAnswers.forEach((answer) => {
          // Convert to number if the question is a slider type
          const question = item.questions.find(
            (q) => q.questionId === answer.questionId
          );

          if (question?.type === 'slider') {
            previousAnswers[answer.questionId] = Number(answer.value);
          } else {
            previousAnswers[answer.questionId] = answer.value;
          }
        });

        setAnswers(previousAnswers);
      }
    }
  }, [surveyDetailData, myDetails]);

  const handleAnswerChange = (questionId: string, value: number | string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = () => {
    if (!companyId || !surveyId || !surveyDetailData?.item) return;

    // Show full page loader
    openLoader();

    const surveyAnswers: SurveyAnswer[] = Object.entries(answers).map(
      ([questionId, value]) => ({
        questionId,
        response: String(value),
      })
    );

    submitAnswers(
      {
        companyId,
        surveyId,
        isAnonymous,
        answers: surveyAnswers,
      },
      {
        onSuccess: async () => {
          // Update local state to mark survey as completed
          if (surveyDetailData?.item) {
            surveyDetailData.item.survey.isCompleted = true;
            surveyDetailData.item.answerAnonymous = isAnonymous;
          }

          // Refetch the survey detail to get the latest data
          await refetch();

          // Close loader before navigating back
          closeLoader();

          // Navigate back
          navigation.goBack();
        },
        onError: () => {
          // Close loader in case of error
          closeLoader();
        },
      }
    );
  };

  const isFormValid = () => {
    if (!surveyDetailData?.item?.questions) return false;

    return surveyDetailData.item.questions
      .filter((q) => q.isRequired)
      .every((q) => {
        // Check if the question has been answered
        const answer = answers[q.questionId];
        return answer !== undefined;
      });
  };

  const renderQuestion = (question: SurveyQuestion) => {
    const { questionId, type, localizedContent } = question;
    // Content is already localized from the server
    const content = localizedContent;

    if (!content) return null;

    switch (type) {
      case 'slider':
        return (
          <SliderQuestion
            key={questionId}
            question={question}
            value={answers[questionId] as number | undefined}
            onChange={(value) => handleAnswerChange(questionId, value)}
          />
        );
      case 'text':
        return (
          <TextQuestion
            key={questionId}
            question={question}
            value={answers[questionId] as string | undefined}
            onChange={(value) => handleAnswerChange(questionId, value)}
          />
        );
      default:
        return null;
    }
  };

  const backAction = useBackNavbarAction();

  if (isLoading) {
    return (
      <Layout leftAction={backAction}>
        <SurveyDetailSkeleton />
      </Layout>
    );
  }

  if (!surveyDetailData?.item) {
    return (
      <Layout leftAction={backAction}>
        <View style={styles.errorContainer}>
          <Paragraph>{t('survey.detail.notFound')}</Paragraph>
        </View>
      </Layout>
    );
  }

  const { survey, questions } = surveyDetailData.item;
  // Content is already localized from the server
  const surveyContent = survey.localizedContent;

  return (
    <Layout
      leftAction={backAction}
      bottomBar={
        <SurveyDetailBottomBar
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          isFormValid={isFormValid()}
          isCompleted={survey.isCompleted}
        />
      }
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.contentContainer}
      >
        <Title>{surveyContent.title}</Title>
        <Paragraph style={styles.description}>
          {surveyContent.description}
        </Paragraph>

        <View style={styles.anonymousContainer}>
          <Paragraph>{t('survey.detail.anonymous')}</Paragraph>
          <Switch value={isAnonymous} onValueChange={setIsAnonymous} />
        </View>

        <View style={styles.questionsContainer}>
          {questions.map(renderQuestion)}
        </View>
      </ScrollView>
    </Layout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 32,
  },
  description: {
    marginTop: 8,
    marginBottom: 8,
  },
  questionsContainer: {
    marginBottom: 38,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  anonymousContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 38,
    paddingVertical: 30,
    borderBottomWidth: 1,
    borderBottomColor: '#E0E0E0',
  },
});
